import { RefObject, useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Modal focus management for WCAG 2.1.2 / 2.4.3:
 *  - Move focus to the first focusable element inside the dialog on open
 *    (respecting an `[autofocus]` hint if present).
 *  - Trap Tab / Shift+Tab within the dialog so keyboard users can't wander
 *    into the visually-hidden underlying page.
 *  - Restore focus to whatever element had it before the dialog opened.
 *
 * Extracted from Modal / Drawer / ConfirmationModal so all three stay in
 * sync — a subtle mismatch here is a keyboard-user-visible bug that axe
 * doesn't catch.
 */
export function useModalFocusTrap(
  isOpen: boolean,
  contentRef: RefObject<HTMLElement | null>
) {
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const root = contentRef.current;
    const target =
      (root?.querySelector('[autofocus]') as HTMLElement | null) ??
      (root?.querySelector(FOCUSABLE_SELECTOR) as HTMLElement | null);
    if (target) setTimeout(() => target.focus(), 0);
    return () => {
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen, contentRef]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const root = contentRef.current;
      if (!root) return;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, contentRef]);
}
